import { createLogger } from "@/lib/logger";
import { type Participant, type Room, RoomEvent } from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";

// UNVERIFIED-MCP: checked against LiveKit byte-stream/RPC docs and
// livekit-client 2.16.1 on 2026-08-05. Recheck when upgrading the SDK.
export const TUTOR_REPLAY_CAPABILITY_ATTRIBUTE = "chicky.tutor_replay";
export const TUTOR_REPLAY_RESYNTHESIS_ATTRIBUTE = "chicky.tutor_replay.resynthesis";
export const TUTOR_REPLAY_RECEIVER_ATTRIBUTE = "chicky.tutor_replay.receive";
export const TUTOR_REPLAY_AVAILABILITY_TOPIC = "chicky.tutor-replay.available.v1";
export const TUTOR_REPLAY_TOPIC = "chicky.tutor-replay.v1";
export const TUTOR_REPLAY_RPC = "chicky.tutor_replay.request.v1";
export const TUTOR_REPLAY_MIME_TYPE = "audio/webm;codecs=opus";
export const TUTOR_REPLAY_PROTOCOL_VERSION = "1";

export const TUTOR_REPLAY_MAX_CLIPS = 20;
export const TUTOR_REPLAY_MAX_BYTES = 10 * 1024 * 1024;
export const TUTOR_REPLAY_MAX_CLIP_BYTES = 2 * 1024 * 1024;

const logger = createLogger("tutor-replay");

export type TutorReplaySource = "exact" | "resynth";

interface TutorReplayClip {
  blob: Blob;
  url: string;
  source: TutorReplaySource;
  interrupted: boolean;
}

export class TutorReplayBlobCache {
  private clips = new Map<string, TutorReplayClip>();
  private totalBytes = 0;

  constructor(
    private readonly maxClips = TUTOR_REPLAY_MAX_CLIPS,
    private readonly maxBytes = TUTOR_REPLAY_MAX_BYTES
  ) {}

  get size() {
    return this.clips.size;
  }

  get bytes() {
    return this.totalBytes;
  }

  get(id: string): TutorReplayClip | undefined {
    const clip = this.clips.get(id);
    if (!clip) return undefined;
    this.clips.delete(id);
    this.clips.set(id, clip);
    return clip;
  }

  has(id: string): boolean {
    return this.clips.has(id);
  }

  set(
    id: string,
    blob: Blob,
    metadata: Pick<TutorReplayClip, "source" | "interrupted">
  ): TutorReplayClip {
    const existing = this.clips.get(id);
    if (existing) {
      URL.revokeObjectURL(existing.url);
      this.totalBytes -= existing.blob.size;
      this.clips.delete(id);
    }

    const clip: TutorReplayClip = {
      blob,
      url: URL.createObjectURL(blob),
      ...metadata,
    };
    this.clips.set(id, clip);
    this.totalBytes += blob.size;
    this.evict();
    return clip;
  }

  clear() {
    for (const clip of this.clips.values()) URL.revokeObjectURL(clip.url);
    this.clips.clear();
    this.totalBytes = 0;
  }

  private evict() {
    while (this.clips.size > this.maxClips || this.totalBytes > this.maxBytes) {
      const oldest = this.clips.entries().next().value as [string, TutorReplayClip] | undefined;
      if (!oldest) return;
      this.clips.delete(oldest[0]);
      this.totalBytes -= oldest[1].blob.size;
      URL.revokeObjectURL(oldest[1].url);
    }
  }
}

export function isTutorReplayIdAvailable(
  cache: TutorReplayBlobCache,
  replayableReplayIds: ReadonlySet<string>,
  replayId: string
): boolean {
  return cache.has(replayId) || replayableReplayIds.has(replayId);
}

export function supportsTutorReplayPlayback(): boolean {
  if (typeof document === "undefined") return false;
  const audio = document.createElement("audio");
  return audio.canPlayType(TUTOR_REPLAY_MIME_TYPE) !== "";
}

export function normalizeTutorReplayText(text: string): string {
  return String(text || "")
    .normalize("NFKC")
    .replace(/\s+/gu, " ")
    .trim();
}

export async function tutorReplayTextKey(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(normalizeTutorReplayText(text));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function tutorReplayOccurrence(
  messages: Array<{ role?: string; text?: string; content?: string }>,
  messageIndex: number
): number {
  const selected = messages[messageIndex];
  const selectedText = normalizeTutorReplayText(selected?.text ?? selected?.content ?? "");
  if (!selectedText) return 0;
  let occurrence = 0;
  for (let index = 0; index < messageIndex; index += 1) {
    const message = messages[index];
    if (
      message?.role === "assistant" &&
      normalizeTutorReplayText(message.text ?? message.content ?? "") === selectedText
    ) {
      occurrence += 1;
    }
  }
  return occurrence;
}

export function tutorReplayMessageToken(text: string, occurrence: number): string {
  return `${normalizeTutorReplayText(text)}:${occurrence}`;
}

export async function buildTutorReplayMessageIds(
  messages: Array<{ role?: string; text?: string; content?: string }>
): Promise<Map<string, string>> {
  const identities = await Promise.all(
    messages.map(async (message, index) => {
      if (message?.role !== "assistant") return null;
      const text = normalizeTutorReplayText(message.text ?? message.content ?? "");
      if (!text) return null;
      const occurrence = tutorReplayOccurrence(messages, index);
      const replayKey = await tutorReplayTextKey(text);
      return [tutorReplayMessageToken(text, occurrence), `${replayKey}:${occurrence}`] as const;
    })
  );

  const replayIds = new Map<string, string>();
  for (const identity of identities) {
    if (identity) replayIds.set(identity[0], identity[1]);
  }
  return replayIds;
}

interface UseTutorReplayOptions {
  room: Room | null;
  agentIdentity?: string;
  enabled: boolean;
  isMuted: boolean;
  setMuted?: (muted: boolean) => void | Promise<void>;
}

interface ReplayRequestResponse {
  status?: "accepted" | "busy" | "forbidden" | "invalid" | "not_found" | "unavailable";
}

interface TutorReplayAvailability {
  type: "tutor_replay_availability";
  protocolVersion: string;
  replayKey: string;
  occurrence: number;
  source: TutorReplaySource;
  available: boolean;
}

function parseTutorReplayAvailability(payload: Uint8Array): TutorReplayAvailability | null {
  try {
    const value = JSON.parse(new TextDecoder().decode(payload)) as Partial<TutorReplayAvailability>;
    if (
      value.type !== "tutor_replay_availability" ||
      value.protocolVersion !== TUTOR_REPLAY_PROTOCOL_VERSION ||
      !/^[0-9a-f]{64}$/.test(value.replayKey || "") ||
      !Number.isInteger(value.occurrence) ||
      Number(value.occurrence) < 0 ||
      (value.source !== "exact" && value.source !== "resynth") ||
      typeof value.available !== "boolean"
    ) {
      return null;
    }
    return value as TutorReplayAvailability;
  } catch {
    return null;
  }
}

interface PendingClip {
  resolve: (clip: TutorReplayClip) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export function useTutorReplay({
  room,
  agentIdentity,
  enabled,
  isMuted,
  setMuted,
}: UseTutorReplayOptions) {
  const [isSupported, setIsSupported] = useState(false);
  const [isResynthesisSupported, setIsResynthesisSupported] = useState(false);
  const [receiverCapable, setReceiverCapable] = useState(false);
  const [preparingReplayId, setPreparingReplayId] = useState<string | null>(null);
  const [playingReplayId, setPlayingReplayId] = useState<string | null>(null);
  const [playingSource, setPlayingSource] = useState<TutorReplaySource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replayableReplayIds, setReplayableReplayIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const cacheRef = useRef(new TutorReplayBlobCache());
  const pendingRef = useRef(new Map<string, PendingClip>());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const restoreMicRef = useRef(false);
  const micTransitionRef = useRef<Promise<void>>(Promise.resolve());
  const roomRef = useRef(room);
  const agentIdentityRef = useRef(agentIdentity);
  const enabledRef = useRef(enabled);
  const isMutedRef = useRef(isMuted);
  const setMutedRef = useRef(setMuted);

  useEffect(() => {
    roomRef.current = room;
    agentIdentityRef.current = agentIdentity;
    enabledRef.current = enabled;
    if (!restoreMicRef.current) isMutedRef.current = isMuted;
    setMutedRef.current = setMuted;
  }, [agentIdentity, enabled, isMuted, room, setMuted]);

  useEffect(() => {
    setReceiverCapable(Boolean(enabled && room && supportsTutorReplayPlayback()));
  }, [enabled, room]);

  const restoreMicrophone = useCallback(async () => {
    if (!restoreMicRef.current) return;
    restoreMicRef.current = false;
    isMutedRef.current = false;
    const setter = setMutedRef.current;
    if (!setter) return;
    micTransitionRef.current = micTransitionRef.current.then(async () => {
      await setter(false);
    });
    await micTransitionRef.current;
  }, []);

  const stopReplay = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingReplayId(null);
    setPlayingSource(null);
    void restoreMicrophone();
  }, [restoreMicrophone]);

  useEffect(() => {
    const syncCapability = () => {
      const participant =
        enabled && room && agentIdentity ? room.remoteParticipants.get(agentIdentity) : undefined;
      const supported =
        receiverCapable &&
        participant?.attributes?.[TUTOR_REPLAY_CAPABILITY_ATTRIBUTE] ===
          TUTOR_REPLAY_PROTOCOL_VERSION;
      setIsSupported(supported);
      setIsResynthesisSupported(
        supported && participant?.attributes?.[TUTOR_REPLAY_RESYNTHESIS_ATTRIBUTE] === "1"
      );
    };
    syncCapability();
    if (!room) return;

    const onAttributesChanged = (
      _changedAttributes: Record<string, string>,
      participant: Participant
    ) => {
      if (participant.identity === agentIdentity) syncCapability();
    };
    const onParticipantChanged = (participant: Participant) => {
      if (participant.identity === agentIdentity) syncCapability();
    };
    room.on(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
    room.on(RoomEvent.ParticipantConnected, onParticipantChanged);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantChanged);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
      room.off(RoomEvent.ParticipantConnected, onParticipantChanged);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantChanged);
    };
  }, [agentIdentity, enabled, receiverCapable, room]);

  useEffect(() => {
    setReplayableReplayIds(new Set());
    if (!room || !enabled || !receiverCapable) return;

    const handleAvailability = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string
    ) => {
      const activeAgentIdentity = agentIdentityRef.current;
      if (
        topic !== TUTOR_REPLAY_AVAILABILITY_TOPIC ||
        !activeAgentIdentity ||
        participant?.identity !== activeAgentIdentity
      ) {
        return;
      }
      const availability = parseTutorReplayAvailability(payload);
      if (!availability) return;
      const replayId = `${availability.replayKey}:${availability.occurrence}`;
      setReplayableReplayIds((current) => {
        const next = new Set(current);
        if (availability.available) next.add(replayId);
        else next.delete(replayId);
        return next;
      });
    };

    room.registerByteStreamHandler(TUTOR_REPLAY_TOPIC, (reader, participantInfo) => {
      void (async () => {
        const activeAgentIdentity = agentIdentityRef.current;
        if (!activeAgentIdentity || participantInfo.identity !== activeAgentIdentity) return;
        const { attributes = {}, mimeType, size } = reader.info;
        if (
          attributes.protocolVersion !== TUTOR_REPLAY_PROTOCOL_VERSION ||
          mimeType !== TUTOR_REPLAY_MIME_TYPE ||
          (typeof size === "number" && size > TUTOR_REPLAY_MAX_CLIP_BYTES)
        ) {
          logger.warn("Rejected invalid tutor replay stream metadata");
          return;
        }

        const replayKey = attributes.replayKey || "";
        const occurrence = Number(attributes.occurrence);
        const source = attributes.source;
        if (
          !/^[0-9a-f]{64}$/.test(replayKey) ||
          !Number.isInteger(occurrence) ||
          occurrence < 0 ||
          (source !== "exact" && source !== "resynth")
        ) {
          logger.warn("Rejected invalid tutor replay identity");
          return;
        }

        try {
          const chunks: Array<ArrayBuffer> = [];
          let receivedBytes = 0;
          for await (const chunk of reader) {
            receivedBytes += chunk.byteLength;
            if (receivedBytes > TUTOR_REPLAY_MAX_CLIP_BYTES) {
              logger.warn("Rejected oversized tutor replay stream");
              return;
            }
            chunks.push(Uint8Array.from(chunk).buffer);
          }
          const blob = new Blob(chunks, { type: TUTOR_REPLAY_MIME_TYPE });
          if (!blob.size || blob.size > TUTOR_REPLAY_MAX_CLIP_BYTES) return;
          const replayId = `${replayKey}:${occurrence}`;
          const clip = cacheRef.current.set(replayId, blob, {
            source,
            interrupted: attributes.interrupted === "true",
          });
          setReplayableReplayIds((current) => {
            const next = new Set(current);
            next.add(replayId);
            return next;
          });
          const pending = pendingRef.current.get(replayId);
          if (pending) {
            clearTimeout(pending.timer);
            pendingRef.current.delete(replayId);
            pending.resolve(clip);
          }
        } catch (streamError) {
          logger.warn("Tutor replay stream failed", { error: String(streamError) });
        }
      })();
    });

    room.on(RoomEvent.DataReceived, handleAvailability);
    void Promise.resolve(
      room.localParticipant.setAttributes({
        [TUTOR_REPLAY_RECEIVER_ATTRIBUTE]: TUTOR_REPLAY_PROTOCOL_VERSION,
      })
    ).catch((attributeError) => {
      logger.warn("Failed to advertise tutor replay receiver capability", {
        error: String(attributeError),
      });
    });

    return () => {
      room.off(RoomEvent.DataReceived, handleAvailability);
      room.unregisterByteStreamHandler(TUTOR_REPLAY_TOPIC);
      void Promise.resolve(
        room.localParticipant.setAttributes({ [TUTOR_REPLAY_RECEIVER_ATTRIBUTE]: "" })
      ).catch(() => undefined);
    };
  }, [enabled, receiverCapable, room]);

  const waitForClip = useCallback((replayId: string) => {
    const cached = cacheRef.current.get(replayId);
    if (cached) return Promise.resolve(cached);
    return new Promise<TutorReplayClip>((resolve, reject) => {
      const timer = setTimeout(() => {
        pendingRef.current.delete(replayId);
        reject(new Error("replay_timeout"));
      }, 15_000);
      pendingRef.current.set(replayId, { resolve, reject, timer });
    });
  }, []);

  const playClip = useCallback(
    async (replayToken: string, clip: TutorReplayClip) => {
      stopReplay();
      if (!isMutedRef.current && setMutedRef.current) {
        const setter = setMutedRef.current;
        isMutedRef.current = true;
        restoreMicRef.current = true;
        micTransitionRef.current = micTransitionRef.current.then(async () => {
          await setter(true);
        });
        await micTransitionRef.current;
      }

      const audio = new Audio(clip.url);
      audioRef.current = audio;
      setPlayingReplayId(replayToken);
      setPlayingSource(clip.source);
      const finish = () => {
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingReplayId(null);
        setPlayingSource(null);
        void restoreMicrophone();
      };
      audio.onended = finish;
      audio.onerror = finish;
      try {
        await audio.play();
      } catch (playbackError) {
        finish();
        throw playbackError;
      }
    },
    [restoreMicrophone, stopReplay]
  );

  const replayTutorMessage = useCallback(
    async (text: string, occurrence: number) => {
      const activeRoom = roomRef.current;
      const destinationIdentity = agentIdentityRef.current;
      if (!enabledRef.current || !activeRoom || !destinationIdentity) return;

      setError(null);
      const replayKey = await tutorReplayTextKey(text);
      const replayId = `${replayKey}:${occurrence}`;
      const replayToken = tutorReplayMessageToken(text, occurrence);
      if (playingReplayId) {
        stopReplay();
        return;
      }

      try {
        let clip = cacheRef.current.get(replayId);
        if (!clip) {
          setPreparingReplayId(replayToken);
          const clipPromise = waitForClip(replayId);
          const responseRaw = await activeRoom.localParticipant.performRpc({
            destinationIdentity,
            method: TUTOR_REPLAY_RPC,
            payload: JSON.stringify({ replayKey, occurrence }),
            responseTimeout: 15_000,
          });
          const response = JSON.parse(responseRaw) as ReplayRequestResponse;
          if (response.status !== "accepted") {
            const arrivedClip = cacheRef.current.get(replayId);
            const pending = pendingRef.current.get(replayId);
            if (pending) {
              clearTimeout(pending.timer);
              pendingRef.current.delete(replayId);
            }
            if (!arrivedClip) throw new Error(response.status || "replay_unavailable");
            clip = arrivedClip;
          } else {
            clip = await clipPromise;
          }
        }
        await playClip(replayToken, clip);
      } catch (replayError) {
        logger.warn("Tutor replay request failed", { error: String(replayError) });
        setError("Couldn't replay this tutor message.");
      } finally {
        setPreparingReplayId((current) => (current === replayToken ? null : current));
      }
    },
    [playClip, playingReplayId, stopReplay, waitForClip]
  );

  const isReplayAvailable = useCallback(
    (replayId: string) => isTutorReplayIdAvailable(cacheRef.current, replayableReplayIds, replayId),
    [replayableReplayIds]
  );

  useEffect(
    () => () => {
      stopReplay();
      cacheRef.current.clear();
      for (const pending of pendingRef.current.values()) {
        clearTimeout(pending.timer);
        pending.reject(new Error("replay_disposed"));
      }
      pendingRef.current.clear();
    },
    [room, stopReplay]
  );

  return {
    isSupported,
    isResynthesisSupported,
    preparingReplayId,
    playingReplayId,
    playingSource,
    replayableReplayIds,
    isReplayAvailable,
    error,
    replayTutorMessage,
    stopReplay,
  };
}
